import React, { useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { alpha, useTheme } from '@mui/material/styles';

import { useClientsQuery, useDeviceSearchQuery } from '@/api/queries';
import type { DeviceSearchEntry } from '@/api/queries';
import type { ClientCoverageSummary, CoverageStatus, StatusFilter } from '@/data/types';
import CoverageBar from '@/components/CoverageBar';
import ExportMenu from '@/components/ExportMenu';
import { useErrorSnackbar } from '@/components/ErrorSnackbar';

const VALID_FILTERS: StatusFilter[] = ['all', 'critical', 'warning', 'healthy', 'below-threshold'];

/** Status dot + label for the status column */
function StatusIndicator({ status }: { status: CoverageStatus }) {
  const theme = useTheme();
  const config: Record<CoverageStatus, { color: string; label: string }> = {
    critical: { color: theme.palette.error.main, label: 'Critical' },
    warning: { color: theme.palette.warning.main, label: 'Warning' },
    healthy: { color: theme.palette.success.main, label: 'Healthy' },
  };
  const { color, label } = config[status];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: color,
          boxShadow: `0 0 6px ${alpha(color, 0.5)}`,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color,
          fontFamily: 'var(--font-mono)',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function ClientListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = React.useState('');

  const statusParam = searchParams.get('status') ?? 'all';
  const statusFilter: StatusFilter = VALID_FILTERS.includes(statusParam as StatusFilter)
    ? (statusParam as StatusFilter)
    : 'all';

  const setStatusFilter = useCallback(
    (value: StatusFilter) => {
      setSearchParams(value === 'all' ? {} : { status: value }, { replace: true });
    },
    [setSearchParams]
  );

  const { data: clients, isLoading, isError } = useClientsQuery();
  const { data: allDevices } = useDeviceSearchQuery();
  const [agentInput, setAgentInput] = React.useState('');
  const { showError } = useErrorSnackbar();
  const theme = useTheme();

  useEffect(() => {
    if (isError) {
      showError('Failed to load client data');
    }
  }, [isError, showError]);

  const agentOptions = useMemo(() => {
    if (!allDevices || agentInput.length < 2) return [];
    const q = agentInput.toLowerCase();
    return allDevices.filter((d) => d.deviceName.toLowerCase().includes(q)).slice(0, 25);
  }, [allDevices, agentInput]);

  const filteredClients = useMemo(() => {
    return (clients ?? []).filter((client) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        client.clientName.toLowerCase().includes(q) || client.companyName.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'below-threshold'
            ? client.status === 'critical' || client.status === 'warning'
            : client.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [clients, searchQuery, statusFilter]);

  const exportData = useMemo(
    () =>
      filteredClients.map((c) => ({
        id: c.id,
        companyName: c.companyName,
        clientName: c.clientName,
        totalDevices: c.totalDevices,
        huntressCount: c.huntressCount,
        threatlockerCount: c.threatlockerCount,
        msp360Count: c.msp360Count,
        addigyCount: c.addigyCount,
        orphanedCount: c.orphanedCount,
        coveragePct: c.coveragePct,
        locationCount: c.locationCount ?? 0,
        status: c.status,
        lastAuditAt: c.lastAuditAt,
      })),
    [filteredClients]
  );

  // Summary chips
  const totalClients = clients?.length ?? 0;
  const criticalCount = clients?.filter((c) => c.status === 'critical').length ?? 0;
  const warningCount = clients?.filter((c) => c.status === 'warning').length ?? 0;

  const columns: GridColDef<ClientCoverageSummary>[] = [
    {
      field: 'companyName',
      headerName: 'Company',
      flex: 2,
      minWidth: 200,
      sortable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '0.8125rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {params.value || params.row.clientName}
        </Typography>
      ),
    },
    {
      field: 'clientName',
      headerName: 'Client Code',
      width: 130,
      sortable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 500,
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'totalDevices',
      headerName: 'Devices',
      width: 85,
      sortable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            fontSize: '0.8125rem',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'huntressCount',
      headerName: 'Huntress',
      width: 90,
      sortable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8125rem',
            color: params.value === params.row.totalDevices ? 'success.main' : 'text.secondary',
            fontWeight: params.value === params.row.totalDevices ? 600 : 400,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'threatlockerCount',
      headerName: 'ThreatLocker',
      width: 110,
      sortable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8125rem',
            color: 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'msp360Count',
      headerName: 'MSP360',
      width: 85,
      sortable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8125rem',
            color: 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'orphanedCount',
      headerName: 'Orphaned',
      width: 95,
      sortable: true,
      renderCell: (params) => {
        const count = params.value as number;
        if (!count)
          return (
            <Typography
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.8125rem',
                color: 'text.secondary',
              }}
            >
              -
            </Typography>
          );
        return (
          <Chip
            label={count}
            size="small"
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.7rem',
              fontWeight: 600,
              height: 20,
              bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
              color: 'warning.main',
              border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.2)}`,
            }}
          />
        );
      },
    },
    {
      field: 'locationCount',
      headerName: 'Locations',
      width: 100,
      type: 'number',
      sortable: true,
      valueFormatter: (value: number | undefined) => (value ? String(value) : '-'),
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.8125rem',
            color: 'text.secondary',
          }}
        >
          {params.value ? params.value : '-'}
        </Typography>
      ),
    },
    {
      field: 'coveragePct',
      headerName: 'Coverage',
      width: 180,
      sortable: true,
      renderCell: (params) => <CoverageBar value={params.value as number} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (params) => <StatusIndicator status={params.value as CoverageStatus} />,
    },
    {
      field: 'lastAuditAt',
      headerName: 'Last Audit',
      width: 120,
      sortable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.7rem',
            color: 'text.secondary',
          }}
        >
          {new Date(params.value as string).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })}
        </Typography>
      ),
    },
  ];

  if (isLoading && !clients) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="text" width={220} height={36} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header + summary */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h5">Client Coverage</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label={`${totalClients} clients`}
            size="small"
            variant="outlined"
            onClick={() => navigate('/clients', { replace: true })}
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.7rem',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              },
            }}
          />
          {criticalCount > 0 && (
            <Chip
              label={`${criticalCount} critical`}
              size="small"
              onClick={() => setStatusFilter(statusFilter === 'critical' ? 'all' : 'critical')}
              sx={{
                cursor: 'pointer',
                bgcolor: alpha(theme.palette.error.main, statusFilter === 'critical' ? 0.25 : 0.08),
                color: 'error.main',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.7rem',
                fontWeight: 600,
                border: `1px solid ${alpha(
                  theme.palette.error.main,
                  statusFilter === 'critical' ? 0.6 : 0.2
                )}`,
                ...(statusFilter === 'critical' && {
                  boxShadow: `0 0 8px ${alpha(theme.palette.error.main, 0.3)}`,
                }),
              }}
            />
          )}
          {warningCount > 0 && (
            <Chip
              label={`${warningCount} warning`}
              size="small"
              onClick={() => setStatusFilter(statusFilter === 'warning' ? 'all' : 'warning')}
              sx={{
                cursor: 'pointer',
                bgcolor: alpha(
                  theme.palette.warning.main,
                  statusFilter === 'warning' ? 0.25 : 0.08
                ),
                color: 'warning.main',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.7rem',
                fontWeight: 600,
                border: `1px solid ${alpha(
                  theme.palette.warning.main,
                  statusFilter === 'warning' ? 0.6 : 0.2
                )}`,
                ...(statusFilter === 'warning' && {
                  boxShadow: `0 0 8px ${alpha(theme.palette.warning.main, 0.3)}`,
                }),
              }}
            />
          )}
        </Box>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          mb: 2,
          mt: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search clients…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 240 }}
        />

        <Autocomplete<DeviceSearchEntry, false>
          size="small"
          options={agentOptions}
          getOptionLabel={(opt) => opt.deviceName}
          inputValue={agentInput}
          onInputChange={(_, value) => setAgentInput(value)}
          onChange={(_, selected) => {
            if (selected) {
              navigate(`/clients/${selected.clientId}/devices`);
              setAgentInput('');
            }
          }}
          filterOptions={(x) => x}
          noOptionsText={agentInput.length < 2 ? 'Type 2+ chars…' : 'No matching agents'}
          renderOption={(props, option) => (
            <li {...props} key={`${option.clientId}-${option.deviceName}`}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 500,
                  }}
                >
                  {option.deviceName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                  }}
                >
                  {option.clientName}
                  {option.hasIssue && (
                    <Box
                      component="span"
                      sx={{ color: 'error.main', fontWeight: 600, ml: 1 }}
                    >
                      has issues
                    </Box>
                  )}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search agents…"
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          sx={{ minWidth: 280 }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="below-threshold">Below Threshold</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="healthy">Healthy</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ ml: 'auto' }}>
          <ExportMenu
            data={exportData}
            filenameBase="client-coverage"
            disabled={filteredClients.length === 0}
          />
        </Box>
      </Box>

      {/* Data Grid */}
      <DataGrid
        rows={filteredClients}
        columns={columns}
        getRowId={(row) => row.id}
        density="compact"
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(`/clients/${params.row.id}/devices`)}
        sx={(t) => ({
          cursor: 'pointer',
          border: `1px solid ${t.palette.divider}`,
          borderRadius: 2.5,
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: alpha(t.palette.primary.main, 0.04),
            ...t.applyStyles('light', {
              bgcolor: alpha(t.palette.primary.main, 0.03),
            }),
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            fontSize: '0.68rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          },
          '& .MuiDataGrid-row': {
            transition: 'background-color 0.1s',
            '&:hover': {
              bgcolor: alpha(t.palette.primary.main, 0.04),
            },
          },
          '& .MuiDataGrid-cell': {
            borderColor: t.palette.divider,
          },
        })}
      />
    </Box>
  );
}
